import React, { useState } from 'react';
import { Button, Form, Input, DatePicker, Typography, Select, Alert, Switch, Modal } from 'antd';
import { EnvironmentOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { 
  setIncidentDate, 
  setAccidentLocation, 
  setIncidentType, 
  setAdditionalSafetyNotes,
  setPoliceCaseNumber,
  setOtherParties,
  setOtherProperty,
  setOtherVehicles,
  setInjuryComplaints,
  setIsFinalized,
  IncidentType
} from '../../../redux/reducers/incidentDetailsSlice';
import { resetIncident } from '../../../redux/store';
import { transformIncidentData } from '../../../core/helpers/transform';
import { UpdateIncidentRequest } from '../../../types/api.types';
import Api from '../../../services/api';
import { photoStorage } from '../../../services/photoStorage';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { areAllRequiredPhotosReady } from '../../../redux/reducers/vehiclePhotosSlice';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface IncidentDetailsFormValues {
  dateTime: Dayjs;
  location: string;
  incidentType: IncidentType;
  description: string;
  policeCaseNumber: string;
  injuryComplaints: boolean;
  otherParties: string;
  otherProperty: string;
  otherVehicles: string;
}

interface IncidentDetailsStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const IncidentDetailsStep: React.FC<IncidentDetailsStepProps> = ({ 
  onNext
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const incidentDetails = useAppSelector((state) => state.incidentDetails);
  const operatorInfo = useAppSelector((state) => state.operatorInfo);
  const vehicle = useAppSelector((state) => state.vehicle);
  const damageLocations = useAppSelector((state) => state.damageLocations);
  const damagedParts = useAppSelector((state) => state.damagedParts);
  const vehiclePhotos = useAppSelector((state) => state.vehiclePhotos);
  const auth = useAppSelector((state) => state.auth);
  const [form] = Form.useForm<IncidentDetailsFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const dispatchFormValues = (values: IncidentDetailsFormValues) => {
    if (values.dateTime) {
      dispatch(setIncidentDate(values.dateTime.toDate()));
    }
    if (values.location) {
      dispatch(setAccidentLocation(values.location));
    }
    if (values.incidentType) {
      dispatch(setIncidentType(values.incidentType));
    }
    if (values.description) {
      dispatch(setAdditionalSafetyNotes(values.description));
    }
    dispatch(setPoliceCaseNumber(values.policeCaseNumber || ''));
    dispatch(setInjuryComplaints(values.injuryComplaints || false));
    dispatch(setOtherParties(values.otherParties || ''));
    dispatch(setOtherProperty(values.otherProperty || ''));
    dispatch(setOtherVehicles(values.otherVehicles || ''));
  };

  const handleSubmit = (values: IncidentDetailsFormValues) => {
    dispatchFormValues(values);
    onNext();
  };

  const handleComplete = async () => {
    try {
      if (!areAllRequiredPhotosReady(vehiclePhotos.photoMap)) {
        Modal.error({
          title: 'Photos Required',
          content: 'Required photos are missing or failed to upload. Go back to Vehicle Photos and retake them.',
        });
        return;
      }

      const values = await form.validateFields();
      dispatchFormValues(values);

      setSubmitting(true);
      const incidentData = transformIncidentData({
        damageLocations,
        damagedParts,
        incidentDetails: {
          ...incidentDetails,
          incidentDate: values.dateTime.toDate(),
          accidentLocationDescription: values.location || incidentDetails.accidentLocationDescription,
          incidentType: values.incidentType || incidentDetails.incidentType,
          additionalSafetyNotes: values.description || incidentDetails.additionalSafetyNotes,
          policeCaseNumber: values.policeCaseNumber || '',
          injuryComplaints: values.injuryComplaints || false,
          otherParties: values.otherParties || '',
          otherProperty: values.otherProperty || '',
          otherVehicles: values.otherVehicles || '',
        },
        vehicle,
        vehiclePhotos,
        operatorInfo,
        auth
      }, true) as UpdateIncidentRequest;
      incidentData.Finalize = true;
      await Api.updateIncident(incidentData);
      dispatch(setIsFinalized(true));

      Modal.success({
        title: 'Success',
        content: 'Incident details added!',
        onOk: async () => {
          dispatch(resetIncident());
          await photoStorage.clearAll();
          navigate('/home');
        },
      });
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      Modal.error({
        title: 'Submission Error',
        content: err instanceof Error ? err.message : 'Failed to submit incident. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    dateTime: incidentDetails.incidentDate ? dayjs(incidentDetails.incidentDate) : dayjs(),
    location: incidentDetails.accidentLocationDescription,
    incidentType: incidentDetails.incidentType,
    description: incidentDetails.additionalSafetyNotes,
    policeCaseNumber: incidentDetails.policeCaseNumber,
    injuryComplaints: incidentDetails.injuryComplaints,
    otherParties: incidentDetails.otherParties,
    otherProperty: incidentDetails.otherProperty,
    otherVehicles: incidentDetails.otherVehicles,
  };

  return (
    <div className="step-container">
      <Title level={5} style={{ textAlign: 'center' }}>Incident Details</Title>

      {operatorInfo.operatorInjured && (
        <Alert
          message="Injury Reported"
          description="Since an injury was reported, please provide detailed information about the incident circumstances."
          type="warning"
          showIcon
          style={{ margin: '20px 0' }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValues}
        style={{ marginTop: 20 }}
      >
          <Form.Item
            label="Date & Time of Incident"
            name="dateTime"
            rules={[{ required: true, message: 'Please select date and time' }]}
          >
            <DatePicker 
              showTime 
              size="large" 
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
          </Form.Item>

          <Form.Item
            label="Incident Type"
            name="incidentType"
            rules={[{ required: true, message: 'Please select incident type' }]}
          >
            <Select size="large">
              {Object.values(IncidentType).map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Police Case #"
            name="policeCaseNumber"
          >
            <Input 
              size="large" 
            />
          </Form.Item>

          <Form.Item
            label="Incident Location and Description"
            name="location"
            rules={[{ required: true, message: 'Please enter location notes' }]}
            extra="Provide more specifics as to where the accident occurred."
          >
            <Input 
              size="large" 
              prefix={<EnvironmentOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="Additional Safety Notes"
            name="description"
            extra={<span style={{ display: 'inline-block', marginTop: 4 }}>Provide more specifics as to any safety notes.</span>}
          >
            <TextArea 
              rows={3} 
              showCount
              maxLength={2000}
            />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span>Injury Complaints?</span>
            <Form.Item
              name="injuryComplaints"
              valuePropName="checked"
              noStyle
            >
              <Switch />
            </Form.Item>
          </div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, marginBottom: 24 }}>
            If yes, please update all injured parties in &apos;Other Parties&apos; box below.
          </div>

          <Form.Item
            label="Other Parties"
            name="otherParties"
            extra="Provide names and contact info for other parties - i.e.: drivers, owners, guest passengers, and witnesses."
          >
            <TextArea 
              rows={3} 
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item
            label="Other Vehicles"
            name="otherVehicles"
            extra="Provide description of other damaged vehicles. i.e.: color, make, model, and damage location."
          >
            <TextArea 
              rows={3} 
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item
            label="Other Property"
            name="otherProperty"
            extra="Provide description of property (other than a vehicle) that was damaged in crash. i.e.: mailbox, stop sign, fire hydrant, etc."
          >
            <TextArea 
              rows={3} 
              maxLength={1000}
            />
          </Form.Item>

        <div className="step-actions" style={{ marginTop: 40 }}>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleComplete}
            loading={submitting}
            size="large"
            style={{ background: '#FF9902', borderColor: '#FF9902', fontWeight: 'bold' }}
          >
            COMPLETE
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default IncidentDetailsStep;